/** Universal databases: DTOs exactly as the API returns them (API.md "Universal databases"). */
import type { Look } from '@/entities/appearance';
import type { MemberRole } from '@/entities/workspace';

export type DatabaseRole = MemberRole;

export const DATABASE_ACTIONS = [
  'database.view',
  'database.edit_records',
  'database.manage_structure',
  'database.edit_details',
  'database.manage_access',
  'database.delete',
] as const;
export type DatabaseAction = (typeof DATABASE_ACTIONS)[number];

export interface Database {
  id: string;
  workspace_id: string;
  name: string;
  /** `null` for a viewer who administers access but may not see the contents. */
  description: string | null;
  /** A color (palette or any #rrggbb) that only identifies the database; `null` = neutral (also for access-only). */
  color: string | null;
  is_confidential: boolean;
  version: number;
  owner_user_id: string;
  owner_name: string;
  created_by_user_id: string | null;
  created_at: string;
  updated_at: string;
  /** `owner` for the workspace owner and the database owner; `null` = powers only. */
  my_role: DatabaseRole | null;
  my_permissions: string[];
  content_visible: boolean;
  /** The viewer's own favorite mark (personal; never set for contents they may not view). */
  is_favorite: boolean;
  /** The section (UD-STRUCT) the database is filed in; `null` = the workspace root. */
  section_id?: string | null;
  /** The look of the database's CATALOG CARD only (never the opened database); `null` = standard. */
  appearance: DatabaseAppearance | null;
  /** Workspace labels on the card, in the database's own order. */
  labels: DatabaseLabel[];
}

export interface DatabaseAppearance {
  v: 1;
  tile: Look | null;
}

export interface DatabaseLabel {
  id: string;
  name: string;
  color: string;
}

export interface DatabaseTable {
  id: string;
  database_id: string;
  name: string;
  /** A color (palette or any #rrggbb) that only identifies the table in the tabs; `null` = neutral. */
  color: string | null;
  version: number;
  created_at: string;
  updated_at: string;
}

/** What a copy did (`POST /databases/{id}/duplicate`, `POST /database-tables/{id}/duplicate`). */
export interface CopyReport {
  tables: number;
  columns: number;
  records: number;
  /** People who are not members of the destination workspace, removed from the copy. */
  dropped_user_references: number;
}

export interface DuplicateDatabaseInput {
  name: string;
  workspace_id?: string;
  include_records: boolean;
  /** Only ever more restrictive; a confidential source is always copied as confidential. */
  is_confidential?: boolean;
  confirm_cross_workspace?: boolean;
}

export interface DuplicateTableInput {
  name?: string;
  database_id?: string;
  include_records: boolean;
  confirm_cross_workspace?: boolean;
}

export const FIELD_TYPES = [
  'text',
  'long_text',
  'number',
  'checkbox',
  'date',
  'datetime',
  'single_select',
  'multi_select',
  'status',
  'tags',
  'user',
] as const;
export type FieldType = (typeof FIELD_TYPES)[number];

export const STATUS_CATEGORIES = ['todo', 'in_progress', 'done'] as const;
export type StatusCategory = (typeof STATUS_CATEGORIES)[number];

export interface FieldOption {
  id: string;
  label: string;
  color: string | null;
  category?: StatusCategory;
}

/** Closed per type: `decimals` (number), `options` (select types), `multiple` (user). */
export interface ColumnConfig {
  decimals?: number;
  options?: FieldOption[];
  multiple?: boolean;
}

export interface DatabaseColumn {
  id: string;
  table_id: string;
  name: string;
  field_type: FieldType;
  config: ColumnConfig;
  /** Wrap long text onto several lines (shared by everybody). */
  wrap_text: boolean;
  /** Stays visible while scrolling horizontally (shared; at most three). */
  frozen: boolean;
  /** Background of the whole column (a palette color or any #rrggbb). */
  color: string | null;
  /** Appearance of the column header shared by everybody (UD-UX3); only what was set. */
  header_style: HeaderStyle;
  version: number;
  created_at: string;
  updated_at: string;
}

export interface TableDetail extends DatabaseTable {
  columns: DatabaseColumn[];
}

/** Canonical stored values (see `cells.ts`): absent key = empty cell. */
export type CellValue = string | boolean | string[];

/** The generic keys of UD-UX3 (`sans` `serif` `mono`) and the named families (Arial, ...). */
export type StyleFont =
  | 'sans'
  | 'serif'
  | 'mono'
  | 'arial'
  | 'times'
  | 'calibri'
  | 'georgia'
  | 'verdana'
  | 'tahoma'
  | 'trebuchet'
  | 'courier';
export type StyleAlign = 'left' | 'center' | 'right';
export type StyleVAlign = 'top' | 'middle' | 'bottom';

/** Text formatting of a cell: every key optional, absent = inherited (UD-UX3). */
export interface CellStyle {
  font?: StyleFont;
  size?: number;
  bold?: boolean;
  italic?: boolean;
  /** `#rrggbb` */
  color?: string;
  align?: StyleAlign;
  valign?: StyleVAlign;
}

export interface HeaderStyle extends CellStyle {
  /** `#rrggbb` */
  background?: string;
  /**
   * Whether a long column NAME wraps onto several lines. Off (absent) = one line cut with an
   * ellipsis. Independent of the column's cell wrapping (`wrap_text`).
   */
  wrap?: boolean;
}

/** A partial change: a value sets the property, `null` removes the override. */
export type StylePatch<T extends CellStyle = CellStyle> = { [K in keyof T]?: T[K] | null };

export interface DatabaseRecord {
  id: string;
  table_id: string;
  /** The record section (UD-SECTIONS); `null` / absent = "No section". */
  section_id?: string | null;
  version: number;
  cells: Record<string, CellValue>;
  /** Background of the row and of single cells (palette colors or any #rrggbb); cell > row > column. */
  color: string | null;
  cell_colors: Record<string, string>;
  /** Explicit per-cell wrapping; a cell without an entry follows its column's `wrap_text`. */
  cell_wrap: Record<string, boolean>;
  /** Explicit text formatting per cell (UD-UX3); a cell without an entry inherits everything. */
  cell_styles: Record<string, CellStyle>;
  created_by_user_id: string | null;
  updated_by_user_id: string | null;
  created_at: string;
  updated_at: string;
}

/** A deleted record in the table's trash (UX-FIX): the record, who deleted it and until when. */
export interface TrashedRecord extends DatabaseRecord {
  trashed_at: string;
  purge_after: string | null;
  trashed_by_name: string | null;
}

export interface TrashedRecordPage {
  items: TrashedRecord[];
  next_cursor: string | null;
  total: number | null;
}

export interface RecordPage {
  items: DatabaseRecord[];
  next_cursor: string | null;
  /** First page only. */
  total: number | null;
}

// --- access administration -----------------------------------------------------------------

export interface DatabaseMember {
  user_id: string;
  display_name: string;
  role: DatabaseRole;
}

export interface DatabaseDenial {
  user_id: string;
  display_name: string;
}

export interface DatabaseException {
  user_id: string;
  display_name: string;
  granted: string[];
  revoked: string[];
}

export type RuleSubjectKind = 'role' | 'team' | 'custom_role';

export interface DatabaseRule {
  id: string;
  subject_kind: RuleSubjectKind;
  subject_role: DatabaseRole | null;
  team_id: string | null;
  custom_role_id: string | null;
  subject_name: string | null;
  role: DatabaseRole;
  suspended: boolean;
  suspended_at: string | null;
}

export interface DatabaseRoleTemplate {
  role: DatabaseRole;
  permissions: string[];
  defaults: string[];
  editable: boolean;
}

/** Why somebody reaches a database (`database_policy.DatabaseAccess.sources`). */
export type AccessSource =
  | 'owner'
  | 'database_owner'
  | 'direct'
  | 'workspace_role'
  | 'custom_role'
  | 'team'
  | 'confidential_view'
  | 'confidential_edit'
  | 'confidential_access';

/** One person with effective access, direct or inherited. */
export interface DatabaseParticipant {
  user_id: string;
  display_name: string;
  role: DatabaseRole | null;
  sources: AccessSource[];
  is_workspace_owner: boolean;
  is_database_owner: boolean;
  has_direct: boolean;
  denied: boolean;
  can_view: boolean;
  exceptions: number;
  /** The acting user may change, remove or deny this person. */
  manageable: boolean;
}

export interface DatabaseAccess {
  database_id: string;
  name: string;
  is_confidential: boolean;
  owner_user_id: string;
  owner_name: string;
  participants: DatabaseParticipant[];
  assignable_roles: DatabaseRole[];
  members: DatabaseMember[];
  denials: DatabaseDenial[];
  exceptions: DatabaseException[];
  rules: DatabaseRule[];
  roles: DatabaseRoleTemplate[];
  catalog: string[];
  required: string[];
}

export interface RuleInput {
  subject_kind: RuleSubjectKind;
  subject_role?: DatabaseRole;
  team_id?: string;
  custom_role_id?: string;
  role: DatabaseRole;
}

export interface OwnerOut {
  database_id: string;
  owner_user_id: string;
  owner_name: string;
  version: number;
}

// --- records query -------------------------------------------------------------------------

export interface RecordFilter {
  column_id: string;
  op: string;
  value?: string;
}

export interface SortLevel {
  column_id: string;
  direction: 'asc' | 'desc';
}

/** Up to this many sort levels (`databases/query.py` MAX_SORTS). */
export const MAX_SORT_LEVELS = 3;

export interface RecordQuery {
  q: string;
  filters: RecordFilter[];
  /** Most significant first; empty = the table's own order. */
  sort: SortLevel[];
}

export const EMPTY_QUERY: RecordQuery = { q: '', filters: [], sort: [] };

// --- saved views -----------------------------------------------------------------------------

/** What a saved view remembers (ids only; `databases/views.py`). */
export interface ViewConfig {
  filters: RecordFilter[];
  sort: SortLevel[];
  hidden: string[];
  order: string[];
  /** How the SAME records are shown (UD-VIEWS); views saved before it are `table`. */
  mode: ViewMode;
  /** Kanban: the status / choice / tags column the board is grouped by (`null` = not chosen). */
  group_column_id: string | null;
  /** Cards / Kanban: the column used as the card title (`null` = automatic). */
  title_column_id: string | null;
  /** How pinned columns are drawn (a presentation of the view); views saved before it are opaque. */
  pinned?: PinnedLook;
  /** Show the table's record sections (UD-SECTIONS); views saved before it show them. */
  show_sections?: boolean;
  /**
   * Cards: `selected` shows the fields of `card_fields` (in that order; `null` = the visible
   * columns, as views saved before it); `all` shows every column. Independent of `hidden`.
   */
  card_fields_mode?: CardFieldsMode;
  card_fields?: string[] | null;
}

export const CARD_FIELDS_MODES = ['selected', 'all'] as const;
export type CardFieldsMode = (typeof CARD_FIELDS_MODES)[number];

export const PINNED_LOOKS = ['opaque', 'transparent'] as const;
export type PinnedLook = (typeof PINNED_LOOKS)[number];

export const VIEW_MODES = ['table', 'cards', 'kanban'] as const;
export type ViewMode = (typeof VIEW_MODES)[number];

/** Per-option record counts of `GET /database-tables/{id}/groups`. */
export interface GroupCounts {
  column_id: string;
  /** DISTINCT matching records - never the sum of the groups (a record may be in several). */
  total: number;
  /** Matching records with no value in the grouping cell (the "No value" group). */
  empty: number;
  groups: { key: string; count: number }[];
}

export interface SavedView {
  id: string;
  table_id: string;
  name: string;
  shared: boolean;
  /** The viewer's own personal view, or a shared one they may change. */
  can_edit: boolean;
  created_by_user_id: string | null;
  version: number;
  /** Already reduced to what still fits the table's current columns. */
  config: ViewConfig;
  /** How many saved settings no longer fit (a column was deleted or changed). */
  adjusted: number;
  created_at: string;
  updated_at: string;
}

// --- record sections (UD-SECTIONS) -------------------------------------------------------------

/** A flat, named divider of ONE table's records (not a workspace section). */
export interface DatabaseSection {
  id: string;
  table_id: string;
  name: string;
  /** Any `#rrggbb`; `null` = neutral. */
  color: string | null;
  version: number;
  created_at: string;
  updated_at: string;
}

/** `total`: the section's records; `matching`: those that also match the search / filters. */
export interface SectionCounts {
  total: number;
  matching: number;
}

export interface SectionWithCounts extends DatabaseSection {
  counts: SectionCounts;
}

export interface SectionList {
  items: SectionWithCounts[];
  /** The system area "No section" (it has no row of its own). */
  unsectioned: SectionCounts;
}

/** The scope of the system area "No section" in a records query / the bands. */
export const NO_SECTION = 'none';
